import React from 'react';

import styled from 'styled-components';

import BuilderCategoryNavRenderer from '~/grid/components/categoryNav/BuilderCategoryNavRenderer';
import ToastContext from '~/shared/components/Toast/ToastContext';
import ToastMessages from '~/shared/components/Toast/ToastMessages';
import featureNames from '~/shared/constants/featureFlags';
import useHasFeature from '~/shared/utils/useHasFeature';

import { useAutoFilters } from '../hooks/useAutofilters';
import useGridInputs from '../hooks/useGridInputs';
import useInGridAssets from '../hooks/useInGridAssets';
import useUnifiedProductGridData from '../hooks/useUnifiedProductGridData';
import { getGridLayoutTrackingTags } from '../utils/tracking';

import DesktopGridFilters from './DesktopGridFilters/DesktopGridFilters';
import DesktopGridHeader from './DesktopGridHeader';
import DesktopGridVirtualScroll from './DesktopGridVirtualScroll';
import DesktopGridDiscoveryGuide from './DiscoveryGuide/DesktopGridDiscoveryGuide';
import GridLayoutTrackingProvider from './GridLayoutTrackingProvider';
import GridSpotlights from './GridSpotlights';
import VirtualScrollGrid from './VirtualScrollGrid';

const LayoutContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1504px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 42px;
  ${(props) => props.$productsGridV2gridV1LayoutSpacingOverride || ''}
  padding-bottom: 42px;
`;

const InnerContainer = styled.div`
  max-width: 1330px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const GridContentWrapper = styled.div`
  display: flex;
  padding: 0 2rem 2rem 2rem;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 268px;
  margin-right: 2rem;
  flex-shrink: 0;
`;

const DesktopGridLayout = () => {
  const {
    products,
    totalNumResults,
    fetchNextPage,
    hasNextPage,
    isPrimaryProductsFetching,
    isPrimaryProductsLoading,
    facets,
    cioFilterName,
    cioFilterValue,
  } = useUnifiedProductGridData();

  const { enabled: showBuilderSideNav } = useHasFeature(
    featureNames.BUILDER_CATEGORY_SIDE_NAV
  );
  const { enabled: isNewPricingUiEnabled } = useHasFeature(
    featureNames.PRICING_UI_AB
  );

  const { mergedAssetsAndProducts } = useInGridAssets({
    products,
    // During the Pricing UI AB test, ignore multi-row or multi-column assets in the column eligibility check
    ignoreMultiRowAndMultiColumnAssets: isNewPricingUiEnabled,
  });
  const {
    builderState,
    gridGender,
    gridType,
    hideSidebar,
    productsGridV2gridV1LayoutSpacingOverride,
    isQuickViewEnabled,
  } = useGridInputs();

  const { autofilterProcessed } = useAutoFilters({
    builderState,
    gridGender,
    facets,
    isPrimaryProductsLoading,
  });

  const { trackingTags } = getGridLayoutTrackingTags({
    totalNumResults,
    cioFilterName,
    cioFilterValue,
    gridType,
  });

  const { sideNav } = useGridInputs();

  return (
    <GridLayoutTrackingProvider>
      <ToastContext>
        <LayoutContainer
          {...trackingTags}
          $productsGridV2gridV1LayoutSpacingOverride={
            productsGridV2gridV1LayoutSpacingOverride
          }
        >
          <ToastMessages />
          <InnerContainer>
            <GridSpotlights />
            <DesktopGridDiscoveryGuide />
            <DesktopGridHeader />
            <GridContentWrapper>
              {!hideSidebar && (
                <SideColumn>
                  {showBuilderSideNav && sideNav?.shouldShowCategoryNav && (
                    <BuilderCategoryNavRenderer />
                  )}
                  {/* To prevent complexity in determining state behavior between the following:
                      1. initial render (before autofilters are applied)
                      2. autofilters applied (after initial render)
                      3. user interaction (after autofilters are applied)
                    Treat the state after autofilters have been applied as the initial render.
                    We don't need to do this for the mobile version due to the batched update behavior and
                    useEffect firing on router change in useSelectedFacetsToApply() */}
                  {autofilterProcessed && <DesktopGridFilters />}
                </SideColumn>
              )}
              {/* Disable new pricing UI for Checkout Interstitial pages (quickview grids) */}
              {isNewPricingUiEnabled && !isQuickViewEnabled ? (
                <VirtualScrollGrid
                  virtualList={mergedAssetsAndProducts}
                  totalNumResults={totalNumResults}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isPrimaryProductsLoading={isPrimaryProductsLoading}
                  isPrimaryProductsFetching={isPrimaryProductsFetching}
                />
              ) : (
                <DesktopGridVirtualScroll
                  virtualList={mergedAssetsAndProducts}
                  totalNumResults={totalNumResults}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isPrimaryProductsLoading={isPrimaryProductsLoading}
                  isPrimaryProductsFetching={isPrimaryProductsFetching}
                />
              )}
            </GridContentWrapper>
          </InnerContainer>
        </LayoutContainer>
      </ToastContext>
    </GridLayoutTrackingProvider>
  );
};

export default DesktopGridLayout;
